import { ToggleGroup, Options } from '@instructure/ui';
import InfoIcon from '@ui/shared/components/Common/InfoIcon';
import {
  Badge,
  Flex,
  View,
  Text,
  IconExpandStartLine,
  Menu,
  IconButton,
  IconSettingsLine,
  Icon,
} from '@smarterservices/smarter-ui';
import useSessionStore from '../../stores/session';
import useAppStore from '../../stores/app';
import WaitingTimer from './WaitingTimer';

/**
 * InReview Component
 * 
 * Displays a collapsible list of sessions that are actively being checked in by a proctor.
 * Shows a timer for how long the session has been in review, the student's name,
 * and displays the reviewing proctor's name in a tooltip.
 * 
 * @param {Object} props - Component props
 * @param {boolean} [props.expanded=true] - Whether the toggle group is expanded by default
 * @param {Function} props.onMenuDismiss - Callback to close the parent popover
 * @returns {JSX.Element} The active check-in component
 */
const InReview = ({ expanded = true, onMenuDismiss }) => {
  const review = useSessionStore((state) => state.review);
  const sessions = useSessionStore((state) => state.sessions);
  const openDrawer = useAppStore((state) => state.openDrawer);
  const focusedSessionSid = useSessionStore((state) => state.focusedSessionSid);

  /**
   * Finds the proctor who is currently reviewing a session
   * @param {string} sessionSid - The session identifier
   * @returns {Object} Object containing the proctor's name and review start timestamp
   */
  const getReviewingProctor = (sessionSid) => {
    const sessionData = sessions?.[sessionSid]?.sessionData;
    if (!sessionData || !sessionData.proctors) {
      return { name: 'Unknown', timestamp: null };
    }

    const reviewingProctor = Object.values(sessionData.proctors).find(
      (proctor) => proctor.inReview === true
    );

    if (reviewingProctor) {
      return {
        name: `${reviewingProctor.firstName} ${reviewingProctor.lastName}`,
        timestamp: reviewingProctor.isInReviewChangeAt
      };
    }

    return { name: 'Unknown', timestamp: null };
  };

  const reviewCount = Object.keys(review).length;

  /**
   * Renders the options menu for a session
   * @param {string} sessionSid - The session identifier
   * @returns {JSX.Element} Menu with check-in and transfer options
   */
  const renderOptionsMenu = (sessionSid) => {
    //Since this there is only an option to join, hide if it is the currently focused session
    if (sessionSid === focusedSessionSid) {
      return null;
    }

    return (
      <Menu
        placement="bottom end"
        shouldContainFocus
        shouldReturnFocus
        offsetX="-5"
        mountNode={() => document.body}
        onDismiss={onMenuDismiss}
        trigger={
          <IconButton
            withBackground={false}
            withBorder={false}
            color="primary"
            size="small"
            screenReaderLabel="Session Options"
            margin="none"
            padding="none"
          >
            <IconSettingsLine />
          </IconButton>
        }
      >
        {/* Start Check-in option */}
        <Menu.Item onSelect={() => openDrawer('focus', sessionSid)}>
          <Flex alignItems="center">
            <Flex.Item>
              <Text>Assist Check-in</Text>
            </Flex.Item>
          </Flex>
        </Menu.Item>
      </Menu>
    );
  };

  return (
    <ToggleGroup
      toggleLabel="Sessions that are being checked in"
      padding="0 0 xxx-small 0"
      defaultExpanded={expanded}
      summary={
        <Flex justifyItems="space-between" margin="none small none none">
          <Flex.Item>Active Check-in <InfoIcon margin="0 0 xxx-small xx-small" color="secondary" tip="This includes sessions that are actively being checked in by a proctor." size="xx-small" /></Flex.Item>
          <Flex.Item>
            <Badge
              count={reviewCount}
              variant="primary"
              standalone
            />
          </Flex.Item>
        </Flex>
      }
    >
      {reviewCount > 0 ? (
        <Options>
          {Object.entries(review).map(([sessionSid, data]) => {
            const proctorInfo = getReviewingProctor(sessionSid);

            return (
              <Options.Item key={`session_review_${sessionSid}`}>
                <Flex justifyItems="space-between">
                  {/* Review timer */}
                  <Flex.Item width="3.2rem">
                    <WaitingTimer startTime={proctorInfo.timestamp || data.routedAt} />
                  </Flex.Item>

                  {/* Student name with reviewing proctor tooltip */}

                  <Flex.Item
                    margin="0 0 0 xx-small"
                    textAlign="start"
                    maxWidth="65%"
                    shouldGrow
                  >
                    {data.firstName} {data.lastName}
                  </Flex.Item>

                  {/* Options menu */}
                  <Flex.Item width="2rem" textAlign="end">
                    {renderOptionsMenu(sessionSid)}
                  </Flex.Item>
                </Flex>
              </Options.Item>
            );
          })}
        </Options>
      ) : (
        <View as="div" padding="small" textAlign="center">
          <Text>No sessions being checked in</Text>
        </View>
      )}
    </ToggleGroup>
  );
};

export default InReview;
